import api from './api.service';
import type { ApiResponse } from '@/types';

function unwrap<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return undefined;
  const r = res as ApiResponse<T>;
  if (r.data !== undefined) return r.data as T;
  return res as T;
}

export type LeadCheckpointItem = {
  key: string;
  category: string;
  label: string;
  pass: boolean;
  detail: string;
};

export type LeadScores = {
  commercial_score: number;
  technical_score: number;
  strategic_score: number;
  financial_score: number;
  risk_factor_score: number;
  lead_quality_score: number;
  enterprise_value_score: number;
  strategic_fit_score: number;
  risk_score: number;
  ai_confidence_score: number;
};

class LeadLifecycleService {
  async getScores(leadId: string) {
    const res = await api.get(`/crm/leads/${leadId}/scores`);
    return unwrap<{ scores: LeadScores; latest_snapshot: unknown }>(res);
  }

  async calculateScore(leadId: string) {
    const res = await api.post(`/crm/leads/${leadId}/calculate-score`, {});
    return unwrap<LeadScores>(res);
  }

  async getDecisionCheckpoint(leadId: string) {
    const res = await api.get(`/crm/leads/${leadId}/decision-checkpoint`);
    return unwrap<{
      checks: LeadCheckpointItem[];
      all_pass: boolean;
      pass_count: number;
      total: number;
    }>(res);
  }

  async saveCheckpointAck(leadId: string, acknowledgements: Record<string, boolean>) {
    const res = await api.post(`/crm/leads/${leadId}/decision-checkpoint`, { acknowledgements });
    return unwrap<{
      checks: LeadCheckpointItem[];
      all_pass: boolean;
      pass_count: number;
      total: number;
    }>(res);
  }

  async getOpportunityIntelligence(opportunityId: string) {
    const res = await api.get(`/crm/opportunities/${opportunityId}/intelligence`);
    return unwrap<{
      similar_projects: unknown[];
      customer_win_rate_pct: number | null;
      risks: string[];
      recommended_next_actions: string[];
      linked_quotations: unknown[];
    }>(res);
  }

  async getOpportunityCheckpoint(opportunityId: string) {
    const res = await api.get(`/crm/opportunities/${opportunityId}/decision-checkpoint`);
    return unwrap<{
      checks: { key: string; label: string; pass: boolean; detail?: string }[];
      all_pass: boolean;
    }>(res);
  }
}

const leadLifecycleService = new LeadLifecycleService();
export default leadLifecycleService;
